import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { usePublishImpersonationTarget } from "@/contexts/ImpersonationTargetContext";

export interface ProjectAdminUser {
  /** Email del usuario: es la llave de `usuarios` (esa tabla no tiene columna `id`). */
  id: string;
  nombre: string;
  email: string;
  rol_nombre: string;
}

interface Ctx {
  impersonatedUser: ProjectAdminUser | null;
  setImpersonatedUser: (u: ProjectAdminUser | null) => void;
  clearImpersonation: () => void;
  isImpersonating: boolean;
}

const ProjectAdminImpersonationContext = createContext<Ctx | null>(null);
const STORAGE_KEY = "sozu-ec-impersonated-project-admin";

export function ProjectAdminImpersonationProvider({ children }: { children: ReactNode }) {
  const [impersonatedUser, setImpersonatedUserState] = useState<ProjectAdminUser | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (impersonatedUser) localStorage.setItem(STORAGE_KEY, JSON.stringify(impersonatedUser));
    else localStorage.removeItem(STORAGE_KEY);
  }, [impersonatedUser]);

  // Publica al núcleo global: menús/rol efectivos y solo lectura de toda la app.
  // Este contexto solo guarda el nombre del rol: se publica sin `rolId`.
  usePublishImpersonationTarget(
    impersonatedUser
      ? {
          email: impersonatedUser.email,
          personaId: null,
          nombre: impersonatedUser.nombre,
          rolId: null,
          rolNombre: impersonatedUser.rol_nombre,
        }
      : null,
  );

  const setImpersonatedUser = useCallback((u: ProjectAdminUser | null) => setImpersonatedUserState(u), []);
  const clearImpersonation = useCallback(() => setImpersonatedUserState(null), []);

  return (
    <ProjectAdminImpersonationContext.Provider
      value={{
        impersonatedUser,
        setImpersonatedUser,
        clearImpersonation,
        isImpersonating: !!impersonatedUser,
      }}
    >
      {children}
    </ProjectAdminImpersonationContext.Provider>
  );
}

export function useProjectAdminImpersonation() {
  const ctx = useContext(ProjectAdminImpersonationContext);
  if (!ctx) throw new Error("useProjectAdminImpersonation must be used within ProjectAdminImpersonationProvider");
  return ctx;
}